import { ArrowUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Input,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Text,
} from '@chakra-ui/react'
import {
  FHRAction,
  FHRCategory,
  FHRDeceleration,
  FHRMethod,
  FHRVariability,
  FetalHeartRateExam,
} from '@hb/shared'
import { useEffect, useRef, useState } from 'react'
import { useField } from 'react-final-form'

// ─── NICHD Auto-Categorization ────────────────────────────────────────────────

/**
 * Derives a NICHD category from the four assessment pillars.
 * https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2009/07/intrapartum-fetal-heart-rate-monitoring
 */
const deriveCategory = (exam: Partial<FetalHeartRateExam>): FHRCategory => {
  const { baselineBpm: bpm, variability, decelerations, accelerations } = exam

  // Category III — predictive of abnormal fetal acid-base status
  if (
    variability === FHRVariability.ABSENT &&
    (decelerations === FHRDeceleration.LATE ||
      decelerations === FHRDeceleration.VARIABLE ||
      decelerations === FHRDeceleration.PROLONGED)
  )
    return FHRCategory.III
  if (bpm !== undefined && bpm < 100 && variability === FHRVariability.ABSENT)
    return FHRCategory.III

  // Category I — strongly predictive of normal acid-base status
  if (
    bpm !== undefined &&
    bpm >= 110 &&
    bpm <= 160 &&
    variability === FHRVariability.MODERATE &&
    accelerations === true &&
    (decelerations === FHRDeceleration.NONE || decelerations === FHRDeceleration.EARLY)
  )
    return FHRCategory.I

  // Category II — indeterminate (everything else)
  return FHRCategory.II
}

// ─── Helpers / constants ──────────────────────────────────────────────────────

const bpmColor = (bpm: number) => {
  if (bpm < 110) return '#E53E3E'
  if (bpm > 160) return '#D69E2E'
  return '#38A169'
}

const bpmLabel = (bpm: number) => {
  if (bpm < 110) return 'Bradycardia'
  if (bpm > 160) return 'Tachycardia'
  return 'Normal'
}

const CATEGORY_META: Record<FHRCategory, { color: string; label: string; sub: string }> = {
  [FHRCategory.I]: {
    color: '#38A169',
    label: 'Category I',
    sub: 'Normal — reassuring fetal status',
  },
  [FHRCategory.II]: {
    color: '#D69E2E',
    label: 'Category II',
    sub: 'Indeterminate — evaluate & monitor',
  },
  [FHRCategory.III]: {
    color: '#E53E3E',
    label: 'Category III',
    sub: 'Abnormal — requires prompt evaluation',
  },
}

const ACTION_OPTIONS = [
  { id: FHRAction.POSITION_CHANGE, label: 'Position change' },
  { id: FHRAction.OXYGEN, label: 'Oxygen' },
  { id: FHRAction.FLUID_BOLUS, label: 'Fluid bolus' },
  { id: FHRAction.IV_ACCESS, label: 'IV access' },
  { id: FHRAction.NOTIFY_PROVIDER, label: 'Notify provider' },
  { id: FHRAction.AMNIOINFUSION, label: 'Amnioinfusion' },
]

const CONCERNING_DECELS = new Set([
  FHRDeceleration.LATE,
  FHRDeceleration.VARIABLE,
  FHRDeceleration.PROLONGED,
])

// ─── Sparkline SVGs ───────────────────────────────────────────────────────────
// Each is a 56×18 path drawn on a 0–18 y-axis (baseline = 9).

const SPARKLINE_PATHS: Record<FHRVariability, string> = {
  [FHRVariability.ABSENT]: 'M0,9 L56,9',
  [FHRVariability.MINIMAL]:
    'M0,9 C4,8 8,10 12,9 C16,8 20,10 24,9 C28,8 32,10 36,9 C40,8 44,10 48,9 L56,9',
  [FHRVariability.MODERATE]:
    'M0,9 C4,4 8,14 12,9 C16,4 20,14 24,9 C28,4 32,14 36,9 C40,4 44,14 48,9 L56,9',
  [FHRVariability.MARKED]:
    'M0,9 C4,1 8,17 12,9 C16,1 20,17 24,9 C28,1 32,17 36,9 C40,1 44,17 48,9 L56,9',
}

const Sparkline = ({ variability, active }: { variability: FHRVariability; active: boolean }) => (
  <svg width="56" height="18" viewBox="0 0 56 18" fill="none">
    <path
      d={SPARKLINE_PATHS[variability]}
      stroke={active ? 'white' : '#718096'}
      strokeWidth={active ? 2 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Category Banner ──────────────────────────────────────────────────────────

const CategoryBanner = () => {
  const { input: catInput } = useField<FHRCategory>('category', { subscription: { value: true } })
  const { input: bpmInput } = useField<number>('baselineBpm', { subscription: { value: true } })
  const { input: varInput } = useField<FHRVariability>('variability', {
    subscription: { value: true },
  })
  const { input: decelInput } = useField<FHRDeceleration>('decelerations', {
    subscription: { value: true },
  })
  const { input: accelInput } = useField<boolean>('accelerations', {
    subscription: { value: true },
  })

  // Auto-update category whenever pillars change
  useEffect(() => {
    const derived = deriveCategory({
      baselineBpm: bpmInput.value,
      variability: varInput.value,
      decelerations: decelInput.value,
      accelerations: accelInput.value,
    })
    if (derived !== catInput.value) catInput.onChange(derived)
  }, [bpmInput.value, varInput.value, decelInput.value, accelInput.value]) // eslint-disable-line react-hooks/exhaustive-deps

  const cat = CATEGORY_META[catInput.value] ? catInput.value : FHRCategory.II
  const { color, label, sub } = CATEGORY_META[cat]

  return (
    <Flex
      w="100%"
      align="center"
      justify="space-between"
      bg="white"
      _dark={{ bg: '#2a2a2a' }}
      borderRadius={14}
      border="1px solid"
      borderColor="gray.200"
      px={4}
      py={3}
      gap={3}>
      <Flex flexFlow="column" gap={0.5}>
        <Text
          fontSize="xs"
          fontFamily="hero-new"
          color="gray.500"
          _dark={{ color: 'gray.300' }}
          fontWeight={500}
          letterSpacing="wider">
          NICHD CATEGORY
        </Text>
        <Text fontSize="xs" color="gray.500" fontFamily="hero-new">
          {sub}
        </Text>
      </Flex>
      <Text fontSize="3xl" fontWeight={700} fontFamily="hero-new" color={color} flexShrink={0}>
        {label}
      </Text>
    </Flex>
  )
}

// ─── Baseline BPM ─────────────────────────────────────────────────────────────

const BPM_MIN = 60
const BPM_MAX = 240

// Sharp color-stop gradient: red (bradycardia) → green (normal) → yellow (tachycardia)
// Breakpoints at 110 and 160 within the 60–240 range
const BPM_RANGE = BPM_MAX - BPM_MIN // 180
const pct = (v: number) => (((v - BPM_MIN) / BPM_RANGE) * 100).toFixed(2)
const bpmGradient = `linear-gradient(to right,
  #FEB2B2 0%, #FEB2B2 ${pct(110)}%,
  #C6F6D5 ${pct(110)}%, #C6F6D5 ${pct(160)}%,
  #FAF089 ${pct(160)}%, #FAF089 100%)`

const BaselineBpmInput = () => {
  const { input } = useField<number>('baselineBpm', { subscription: { value: true } })
  const bpm = input.value ?? 140
  const color = bpmColor(bpm)
  const label = bpmLabel(bpm)

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const adjust = (delta: number) =>
    input.onChange(Math.min(BPM_MAX, Math.max(BPM_MIN, bpm + delta)))

  const startEditing = () => {
    setEditValue(String(bpm))
    setIsEditing(true)
  }

  const commitEdit = () => {
    const parsed = parseInt(editValue, 10)
    if (!isNaN(parsed)) input.onChange(Math.min(BPM_MAX, Math.max(BPM_MIN, parsed)))
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  return (
    <Flex flexFlow="column" w="100%" gap={3} align="center">
      <Flex w="100%" justify="space-between" align="center">
        <SectionLabel>BASELINE BPM</SectionLabel>
        <Text fontSize="md" fontWeight={600} fontFamily="hero-new" color={color}>
          {label}
        </Text>
      </Flex>
      {/* Big BPM display — tap to edit manually */}
      {isEditing ? (
        <Input
          ref={inputRef}
          variant="unstyled"
          type="number"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          fontSize="5xl"
          fontWeight={700}
          fontFamily="hero-new"
          color={color}
          textAlign="center"
          w="160px"
          borderBottom="2px solid"
          borderColor={color}
          borderRadius={0}
          px={1}
        />
      ) : (
        <Text
          fontSize="5xl"
          fontWeight={700}
          fontFamily="hero-new"
          color={color}
          lineHeight={1}
          transition="color 0.2s ease"
          cursor="pointer"
          userSelect="none"
          _hover={{ opacity: 0.7 }}
          onClick={startEditing}>
          {bpm}
        </Text>
      )}
      <Text
        fontSize="md"
        color="gray.500"
        _dark={{ color: 'gray.400' }}
        fontFamily="hero-new"
        mt={-1}>
        bpm
      </Text>
      {/* Slider */}
      <Box w="100%" pb={6}>
        <Slider
          focusThumbOnChange={false}
          min={BPM_MIN}
          max={BPM_MAX}
          step={1}
          value={bpm}
          onChange={input.onChange}>
          {/* Bound marks */}
          <SliderMark value={BPM_MIN} mt={4} fontSize="xs" fontFamily="hero-new" color="gray.400">
            {BPM_MIN}
          </SliderMark>
          <SliderMark
            value={BPM_MAX}
            mt={4}
            transform="translateX(-100%)"
            fontSize="xs"
            fontFamily="hero-new"
            color="gray.400">
            {BPM_MAX}
          </SliderMark>
          {/* Clinical threshold marks */}
          <SliderMark
            value={110}
            mt={4}
            transform="translateX(-50%)"
            fontSize="xs"
            fontFamily="hero-new"
            color="#E53E3E">
            110
          </SliderMark>
          <SliderMark
            value={160}
            mt={4}
            transform="translateX(-50%)"
            fontSize="xs"
            fontFamily="hero-new"
            color="#D69E2E">
            160
          </SliderMark>
          <SliderTrack
            h="20px"
            borderRadius="full"
            border="2px solid"
            borderColor="gray.200"
            bg={bpmGradient}
            _dark={{ borderColor: 'gray.600' }}>
            <SliderFilledTrack bg="whiteAlpha.400" />
          </SliderTrack>
          <SliderThumb h="32px" w="32px" borderRadius="full" boxShadow="md" />
        </Slider>
      </Box>
      {/* Fine-tune buttons */}
      <Flex gap={2}>
        {([-5, -1, 1, 5] as const).map(delta => (
          <Button
            key={delta}
            h="44px"
            minW="52px"
            px={3}
            variant="outline"
            borderRadius={10}
            fontSize="md"
            fontFamily="hero-new"
            fontWeight={500}
            onClick={() => adjust(delta)}>
            {delta > 0 ? `+${delta}` : delta}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}

// ─── Variability ──────────────────────────────────────────────────────────────

const VARIABILITY_OPTIONS: {
  id: FHRVariability
  label: string
  sub: string
}[] = [
  { id: FHRVariability.ABSENT, label: 'Absent', sub: 'undetectable' },
  { id: FHRVariability.MINIMAL, label: 'Minimal', sub: '≤5 bpm' },
  { id: FHRVariability.MODERATE, label: 'Moderate', sub: '6–25 bpm' },
  { id: FHRVariability.MARKED, label: 'Marked', sub: '>25 bpm' },
]

const VariabilityInput = () => {
  const { input } = useField<FHRVariability>('variability', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <SectionLabel>VARIABILITY</SectionLabel>
      <Flex gap={2} w="100%">
        {VARIABILITY_OPTIONS.map(opt => {
          const isActive = input.value === opt.id
          const isAbsent = opt.id === FHRVariability.ABSENT
          const isMarked = opt.id === FHRVariability.MARKED
          const scheme = isAbsent ? 'red' : isMarked ? 'orange' : isActive ? 'teal' : 'gray'
          return (
            <Button
              key={opt.id}
              flex={1}
              h="72px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? scheme : 'gray'}
              borderRadius={10}
              px={1}
              onClick={() => input.onChange(opt.id)}>
              <Flex flexFlow="column" align="center" gap={1}>
                <Sparkline variability={opt.id} active={isActive} />
                <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                  {opt.label}
                </Text>
                <Text fontSize="sm" opacity={0.7} fontFamily="hero-new">
                  {opt.sub}
                </Text>
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Accelerations ────────────────────────────────────────────────────────────

const AccelerationsInput = () => {
  const { input } = useField<boolean>('accelerations', { subscription: { value: true } })
  const isPresent = input.value
  const isAbsent = !input.value

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>ACCELERATIONS</SectionLabel>
      <Flex gap={3} w="100%">
        <Button
          flex={1}
          h="52px"
          variant={isPresent ? 'solid' : 'outline'}
          colorScheme={isPresent ? 'teal' : 'gray'}
          gap={1.5}
          borderRadius={10}
          fontFamily="hero-new"
          fontWeight={isPresent ? 600 : 400}
          onClick={() => input.onChange(true)}>
          <ArrowUpIcon width={5} height={5} />
          <Text>Present</Text>
        </Button>
        <Button
          flex={1}
          h="52px"
          variant={isAbsent ? 'solid' : 'outline'}
          colorScheme={isAbsent ? 'orange' : 'gray'}
          borderRadius={10}
          fontFamily="hero-new"
          fontWeight={isAbsent ? 600 : 400}
          onClick={() => input.onChange(false)}>
          Absent
        </Button>
      </Flex>
    </Flex>
  )
}

// ─── Decelerations ────────────────────────────────────────────────────────────

const DECEL_OPTIONS: {
  id: FHRDeceleration
  label: string
  sub: string
  scheme: string
}[] = [
  { id: FHRDeceleration.NONE, label: 'None', sub: '', scheme: 'teal' },
  { id: FHRDeceleration.EARLY, label: 'Early', sub: 'uniform / gradual', scheme: 'blue' },
  { id: FHRDeceleration.VARIABLE, label: 'Variable', sub: 'abrupt / abrupt', scheme: 'orange' },
  { id: FHRDeceleration.LATE, label: 'Late', sub: 'gradual / after peak', scheme: 'red' },
  { id: FHRDeceleration.PROLONGED, label: 'Prolonged', sub: '>2 min', scheme: 'red' },
]

const ACTION_CHIP_COLORS: Record<FHRAction, string> = {
  [FHRAction.POSITION_CHANGE]: '#4299E1',
  [FHRAction.OXYGEN]: '#48BB78',
  [FHRAction.FLUID_BOLUS]: '#9F7AEA',
  [FHRAction.IV_ACCESS]: '#ED8936',
  [FHRAction.NOTIFY_PROVIDER]: '#E53E3E',
  [FHRAction.AMNIOINFUSION]: '#0BC5EA',
}

const DecelerationsInput = () => {
  const { input } = useField<FHRDeceleration>('decelerations', { subscription: { value: true } })
  const { input: actionsInput } = useField<FHRAction[]>('actions', {
    subscription: { value: true },
  })
  const isConcerning = CONCERNING_DECELS.has(input.value)

  return (
    <Flex flexFlow="column" w="100%" gap={3}>
      <SectionLabel>DECELERATIONS</SectionLabel>
      <Flex gap={2} wrap="wrap" w="100%" justify="center">
        {DECEL_OPTIONS.map(opt => {
          const isActive = input.value === opt.id
          return (
            <Button
              key={opt.id}
              minW="100px"
              h={opt.sub ? '56px' : '48px'}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? opt.scheme : 'gray'}
              borderRadius={10}
              onClick={() => {
                input.onChange(opt.id)
                if (opt.id === FHRDeceleration.NONE || opt.id === FHRDeceleration.EARLY) {
                  actionsInput.onChange([])
                }
              }}>
              <Flex flexFlow="column" align="center" gap={0}>
                <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                  {opt.label}
                </Text>
                {opt.sub ? (
                  <Text fontSize="14px" opacity={0.75} fontFamily="hero-new">
                    {opt.sub}
                  </Text>
                ) : null}
              </Flex>
            </Button>
          )
        })}
      </Flex>

      {/* Action Required alert for concerning decelerations */}
      {isConcerning ? (
        <Flex
          flexFlow="column"
          w="100%"
          gap={2}
          bg="red.50"
          _dark={{ bg: 'rgba(229,62,62,0.12)' }}
          border="1px solid"
          borderColor="red.200"
          borderRadius={12}
          p={3}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" fontWeight={700} fontFamily="hero-new" color="red.600">
              ⚠ Action Required
            </Text>
            <Text fontSize="xs" color="red.500" fontFamily="hero-new">
              Document interventions taken
            </Text>
          </Flex>
          <Flex justify="center" gap={2} wrap="wrap">
            {ACTION_OPTIONS.map(action => (
              <Button
                key={action.id}
                px={3}
                py={2}
                h="auto"
                borderRadius={20}
                border="1.5px solid"
                borderColor={ACTION_CHIP_COLORS[action.id] ?? 'gray.400'}
                color={
                  actionsInput.value?.includes(action.id)
                    ? 'white'
                    : (ACTION_CHIP_COLORS[action.id] ?? 'gray.600')
                }
                onClick={() => {
                  if (actionsInput.value?.includes(action.id)) {
                    actionsInput.onChange(actionsInput.value?.filter(id => id !== action.id))
                  } else {
                    actionsInput.onChange([...(actionsInput.value ?? []), action.id])
                  }
                }}
                fontSize="sm"
                fontFamily="hero-new"
                fontWeight={500}
                bg={
                  actionsInput.value?.includes(action.id)
                    ? (ACTION_CHIP_COLORS[action.id] ?? 'gray.400')
                    : 'white'
                }
                _dark={{
                  bg: actionsInput.value?.includes(action.id)
                    ? (ACTION_CHIP_COLORS[action.id] ?? 'gray.400')
                    : '#2a2a2a',
                }}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}>
                {action.label}
              </Button>
            ))}
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  )
}

// ─── Monitoring Method ────────────────────────────────────────────────────────

const METHOD_OPTIONS: { id: FHRMethod; label: string; sub: string }[] = [
  { id: FHRMethod.DOPPLER, label: 'Doppler', sub: 'external' },
  { id: FHRMethod.FETOSCOPE, label: 'Fetoscope', sub: 'auscultation' },
  { id: FHRMethod.INTERNAL_SCALP_ELECTRODE, label: 'ISE', sub: 'internal' },
]

const MethodInput = () => {
  const { input } = useField<FHRMethod>('method', { subscription: { value: true } })
  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>MONITORING METHOD</SectionLabel>
      <Flex gap={2} w="100%">
        {METHOD_OPTIONS.map(opt => {
          const isActive = input.value === opt.id
          return (
            <Button
              key={opt.id}
              flex={1}
              h="56px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'blue' : 'gray'}
              borderRadius={10}
              onClick={() => input.onChange(opt.id)}>
              <Flex flexFlow="column" align="center" gap={0}>
                <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                  {opt.label}
                </Text>
                <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                  {opt.sub}
                </Text>
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const FHREntry = () => (
  <Center pb={8} w="100%">
    <Flex flexFlow="column" w="100%" maxW="520px" gap={5} py={4} px={2} align="center">
      <CategoryBanner />
      <Divider />
      <BaselineBpmInput />
      <Divider />
      <VariabilityInput />
      <Divider />
      <AccelerationsInput />
      <Divider />
      <DecelerationsInput />
      <Divider />
      <MethodInput />
    </Flex>
  </Center>
)
